import React, { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { utils, write, writeFileXLSX } from "xlsx";
import { Button, message, Tooltip } from "antd";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  ContainerDiv,
  Footer,
  LoadingIcon,
  RipenessCriteria,
  Sidebar,
} from "../../components";
import { COLORS } from "../../consts";
import LineChart from "./LineChart";
import Dropdowns from "./Dropdowns";
import Subplots from "./Subplots";
import Metrics from "./Metrics";
import {
  getGraphData,
  getSubplotData,
  getSubplotDataSize,
  getSubplotGraphData,
} from "../../utils/api";

const DEFAULT_PAGE_SIZE = 10;

const Plots = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteMetadata, setSiteMetadata] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [currentSite, setCurrentSite] = useState(null);
  const [currentPlot, setCurrentPlot] = useState(null);
  const [plotData, setPlotData] = useState([]);
  const [currentCrop, setCurrentCrop] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const [currentMetric, setCurrentMetric] = useState(null);
  const [metricType, setMetricType] = useState(null);
  const [metricData, setMetricData] = useState([]);
  const [metricVals, setMetricVals] = useState({});
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const [graphData, setGraphData] = useState({});
  const [graphLoading, setGraphLoading] = useState(false);
  const [lineData, setLineData] = useState({});
  const [isCumulative, setIsCumulative] = useState(false);

  const [subplotData, setSubplotData] = useState([]);
  const [subplotLoading, setSubplotLoading] = useState(false);
  const [fetchingAllSubplots, setFetchingAllSubplots] = useState(false);
  const [subplotCount, setSubplotCount] = useState(0);
  const [subplotPage, setSubplotPage] = useState(1);

  const [subplotGraphData, setSubplotGraphData] = useState({});
  const [subplotLineData, setSubplotLineData] = useState([]);
  const [subplotGraphLoading, setSubplotGraphLoading] = useState(false);
  const [isCumulativeSubplot, setIsCumulativeSubplot] = useState(false);

  // CurrentOrg - id of the org
  // CurrentSite - {name, id , metadata}
  // CurrentCrop - {name, id}
  // CurrentPlot = {name, id}
  // CurrentMetric - name of the metric

  const isDictionary = (variable) => {
    return (
      typeof variable === "object" &&
      variable !== null &&
      !Array.isArray(variable)
    );
  };

  const getMetricValue = (metric) => {
    if (typeof metric === "string") {
      return metric;
    } else if (isDictionary(metric)) {
      return metric.value;
    }
    return "Unknown Metric";
  };

  // Fetch graph data
  const fetchGraphData = async () => {
    try {
      setGraphLoading(true);
      const graphRes = await getGraphData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        currentPlot.id,
      );
      const data = graphRes?.data?.data;
      setGraphData(data || []);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Oops! Error fetching graph data!";
      message.open({
        type: "error",
        content: msg,
      });
    } finally {
      setGraphLoading(false);
    }
  };

  // Fetch subplot line graph data
  const fetchSubplotGraphData = async () => {
    try {
      setSubplotGraphLoading(true);
      const graphRes = await getSubplotGraphData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        currentPlot.id,
      );
      const data = graphRes?.data?.data;
      setSubplotGraphData(data || []);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Oops! Error fetching subplot graph data!";
      message.open({
        type: "error",
        content: msg,
      });
    } finally {
      setSubplotGraphLoading(false);
    }
  };

  // Fetch subplot page count
  const fetchSubplotDataSize = async (org, site, crop, plot, date) => {
    try {
      setSubplotLoading(true);
      const subplotRes = await getSubplotDataSize(
        org,
        site.id,
        crop.id,
        plot.id,
        date,
      );
      const count = subplotRes?.data?.data?.count;

      setSubplotCount(count || 0);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Oops! Error fetching subplot data!";
      message.open({
        type: "error",
        content: msg,
      });
    } finally {
      setSubplotLoading(false);
    }
  };

  // Fetch subplot data
  const fetchSubplotData = async (
    org,
    site,
    crop,
    plot,
    date,
    page = 1,
    page_size = DEFAULT_PAGE_SIZE,
  ) => {
    try {
      setSubplotLoading(true);
      const subplotRes = await getSubplotData(
        org,
        site.id,
        crop.id,
        plot.id,
        date,
        page,
        page_size,
      );
      const data = subplotRes?.data?.data;
      setSubplotData(data || []);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Oops! Error fetching subplot data!";
      message.open({
        type: "error",
        content: msg,
      });
    } finally {
      setSubplotLoading(false);
    }
  };

  // Set line graph data
  useEffect(() => {
    if (getMetricValue(currentMetric)) {
      const sortedData = {};
      Object.entries(graphData).forEach((entry) => {
        sortedData[entry[0]] = entry[1].sort((a, b) =>
          a.harvest_date.localeCompare(b.harvest_date),
        );
      });

      const dates = Object.values(sortedData)[0]?.map(
        (lineData) => lineData.harvest_date,
      );
      const res = [];
      for (const [plotName, plotData] of Object.entries(sortedData)) {
        const line = { name: plotName };
        let valToAdd = 0;
        line.val = plotData?.map((data) => {
          if (data?.harvest_date === currentDate) {
            setMetricVals(data.data);
          }

          if (
            isCumulative &&
            siteMetadata &&
            siteMetadata.cumulative_metrics.includes(
              getMetricValue(currentMetric),
            )
          ) {
            let ans = data?.data[getMetricValue(currentMetric)]
              ? data?.data[getMetricValue(currentMetric)] + valToAdd
              : null;
            valToAdd += data?.data[getMetricValue(currentMetric)] || 0;
            return ans;
          } else {
            return data?.data[getMetricValue(currentMetric)] || null;
          }
        });
        res.push(line);
      }
      setLineData({ labels: dates, data: res });
    } else {
      setLineData({});
    }
  }, [currentMetric, currentDate, graphData, siteMetadata, isCumulative]);

  // Set subplot line graph data
  useEffect(() => {
    if (getMetricValue(currentMetric)) {
      const sortedData = {};
      Object.entries(subplotGraphData).forEach((entry) => {
        sortedData[entry[0]] = entry[1].sort((a, b) =>
          a.harvest_date.localeCompare(b.harvest_date),
        );
      });

      const dates = Object.values(sortedData)[0]?.map(
        (lineData) => lineData.harvest_date,
      );

      const res = [];
      for (const [plotName, plotData] of Object.entries(sortedData)) {
        const line = { name: plotName };
        let valToAdd = 0;
        line.val = plotData?.map((data) => {
          if (
            isCumulativeSubplot &&
            siteMetadata &&
            siteMetadata.cumulative_metrics.includes(
              getMetricValue(currentMetric),
            )
          ) {
            let ans = data?.data[getMetricValue(currentMetric)]
              ? data?.data[getMetricValue(currentMetric)] + valToAdd
              : null;
            valToAdd += data?.data[getMetricValue(currentMetric)] || 0;
            return ans;
          } else {
            return data?.data[getMetricValue(currentMetric)] || null;
          }
        });
        res.push(line);
      }
      setSubplotLineData({ labels: dates, data: res });
    } else {
      setSubplotLineData({});
    }
  }, [currentMetric, subplotGraphData, siteMetadata, isCumulativeSubplot]);

  // fetch subplot data on dropdown change
  useEffect(() => {
    const fn = async () => {
      try {
        if (
          currentOrg &&
          currentSite &&
          currentCrop &&
          currentPlot &&
          currentDate
        ) {
          await fetchSubplotDataSize(
            currentOrg,
            currentSite,
            currentCrop,
            currentPlot,
            currentDate,
          );

          await fetchSubplotData(
            currentOrg,
            currentSite,
            currentCrop,
            currentPlot,
            currentDate,
          );
          setSubplotPage(1);
        } else {
          setSubplotData([]);
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message || "Oops! Error fetching subplot data!";
        messageApi.open({
          type: "error",
          content: msg,
        });
      }
    };
    fn();
  }, [currentPlot, currentDate]);

  const handleSubplotPageChange = async (page) => {
    console.log("HANDLING PAGE CHANGE");
    setSubplotPage(page);
    try {
      await fetchSubplotData(
        currentOrg,
        currentSite,
        currentCrop,
        currentPlot,
        currentDate,
        page,
        DEFAULT_PAGE_SIZE,
      );
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Oops! Error fetching subplot data!";
      messageApi.open({
        type: "error",
        content: msg,
      });
    }
  };

  // Download to Excel
  const exportFile = useCallback(() => {
    const wb = utils.book_new();

    subplotData.forEach((subplot) => {
      const fruitData = subplot?.fruit_data || [];

      // Flatten the data
      const flattenedData = fruitData.flatMap((item) => {
        const { Fruits, ...rest } = item;
        return Fruits.map((fruit) => ({ ...rest, ...fruit }));
      });

      let ws = XLSX.utils.json_to_sheet(flattenedData);
      utils.book_append_sheet(wb, ws, subplot.name);
    });
    writeFileXLSX(wb, `data_${currentPlot.name}_${currentDate}.xlsx`);
  }, [subplotData, currentPlot, currentDate]);

  const s2ab = (s) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) {
      view[i] = s.charCodeAt(i) & 0xff;
    }
    return buf;
  };

  function truncateString(inputString) {
    if (inputString.length > 30) {
      return inputString.substring(0, 30);
    }
    return inputString;
  }

  const exportFiles = useCallback(async () => {
    setFetchingAllSubplots(true);
    message.info("Fetching subplots...");
    const promiseArr = plotData.map((plot) => {
      return getSubplotData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        plot.id,
        currentDate,
      );
    });
    try {
      const vals = await Promise.all(promiseArr);

      let zip = new JSZip();
      vals.forEach((val, idx) => {
        const wb = utils.book_new();
        val?.data?.data.forEach((subplot) => {
          // console.log({ subplot });
          const fruitData = subplot?.fruit_data || [];

          // Flatten the data
          const flattenedData = fruitData.flatMap((item) => {
            // Create a copy of the item object
            let itemCopy = { ...item };

            // Delete the Fruits property from the copy
            delete itemCopy.Fruits;

            // Return the new array
            return item.Fruits.map((fruit) => ({ ...fruit, ...itemCopy }));
          });
          const ws = utils.json_to_sheet(flattenedData);
          utils.book_append_sheet(wb, ws, truncateString(subplot.name));
        });

        if (wb.SheetNames.length) {
          const wbout = write(wb, { bookType: "xlsx", type: "binary" });
          const blob = new Blob([s2ab(wbout)], {
            type: "application/octet-stream",
          });

          const fileNameExcel = `data_${plotData[idx]?.name}_${currentDate}.xlsx`;
          zip.file(fileNameExcel, blob);
        }
      });
      zip.generateAsync({ type: "blob" }).then(function (blob) {
        saveAs(blob, `plots_${currentSite?.id}.zip`);
      });
    } catch (err) {
      console.log(err);
      message.error("Error fetching plot data!");
    } finally {
      setFetchingAllSubplots(false);
    }
  }, [
    currentOrg,
    currentSite,
    currentPlot,
    currentCrop,
    currentDate,
    plotData,
  ]);

  return (
    <ContainerDiv>
      {contextHolder}
      <div id="plots">
        <Sidebar active="plots" open={sidebarOpen} setOpen={setSidebarOpen} />
        <div
          className={`flex flex-col w-full px-20 ${
            sidebarOpen ? "pl-60" : ""
          } pt-10 md:pt-4 pb-14 md:px-15 sm:pr-10 justify-center transition-padding ease-in-out duration-200`}
        >
          <Dropdowns
            setSiteMetadata={setSiteMetadata}
            setCurrentMetric={setCurrentMetric}
            setMetricType={setMetricType}
            currentOrg={currentOrg}
            setCurrentOrg={setCurrentOrg}
            currentSite={currentSite}
            setCurrentSite={setCurrentSite}
            currentCrop={currentCrop}
            setCurrentCrop={setCurrentCrop}
            currentPlot={currentPlot}
            setCurrentPlot={setCurrentPlot}
            plotData={plotData}
            setPlotData={setPlotData}
            fetchGraphData={fetchGraphData}
            fetchSubplotGraphData={fetchSubplotGraphData}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            setMetricData={setMetricData}
            setDropdownLoading={setDropdownLoading}
          />

          {Object.keys(siteMetadata).length === 0 && !dropdownLoading ? (
            <div className="text-2xl text-center">Metadata not Configured!</div>
          ) : (
            <>
              {!dropdownLoading ? (
                <div className="flex ml-auto" id="downloadButtons">
                  <Tooltip
                    title={
                      subplotLoading || !subplotData.length
                        ? ""
                        : `Export ${currentPlot?.name} details for ${currentCrop?.name} on ${currentDate} to excel`
                    }
                    color={COLORS.primaryBlue}
                  >
                    <Button
                      type="primary"
                      icon={
                        subplotLoading ? (
                          <LoadingOutlined />
                        ) : (
                          <DownloadOutlined />
                        )
                      }
                      onClick={exportFile}
                      disabled={subplotLoading || !subplotData.length}
                      className="mr-2 rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
                    >
                      Export Selected Plot
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title={
                      fetchingAllSubplots || !plotData.length
                        ? ""
                        : `Export all plot details for ${currentCrop?.name} on ${currentDate} to zip`
                    }
                    color={COLORS.primaryBlue}
                  >
                    <Button
                      type="primary"
                      icon={
                        fetchingAllSubplots ? (
                          <LoadingOutlined />
                        ) : (
                          <DownloadOutlined />
                        )
                      }
                      className="rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
                      onClick={exportFiles}
                      disabled={fetchingAllSubplots || !plotData.length}
                    >
                      Export All Plots
                    </Button>
                  </Tooltip>
                </div>
              ) : null}

              {siteMetadata?.ripe_criteria && (
                <RipenessCriteria
                  metaData={siteMetadata.ripe_criteria}
                  fetchGraphData={fetchGraphData}
                  currentPlot={currentPlot}
                  currentCrop={currentCrop}
                  currentDate={currentDate}
                />
              )}
              <div className="flex sm:flex-col">
                {metricData.length ? (
                  <Metrics
                    metricData={metricData}
                    currentMetric={currentMetric}
                    setCurrentMetric={setCurrentMetric}
                    setMetricType={setMetricType}
                    metricVals={metricVals}
                    metricUnits={siteMetadata?.units}
                  />
                ) : null}

                {!graphLoading ? (
                  <>
                    {lineData?.data?.length ? (
                      <LineChart
                        classes="plotLine"
                        lineData={lineData}
                        currentMetric={currentMetric}
                        siteMetadata={siteMetadata}
                        isCumulative={isCumulative}
                        setIsCumulative={setIsCumulative}
                        maxWidth={750}
                      />
                    ) : null}
                  </>
                ) : (
                  <LoadingIcon height="200px" />
                )}
              </div>
              {!subplotGraphLoading ? (
                <>
                  {siteMetadata?.show_subplot_time_series_graph &&
                  lineData?.data?.length ? (
                    <LineChart
                      classes="subplotLine"
                      lineData={subplotLineData}
                      currentMetric={currentMetric}
                      siteMetadata={siteMetadata}
                      isCumulative={isCumulativeSubplot}
                      setIsCumulative={setIsCumulativeSubplot}
                      isSubplot={true}
                    />
                  ) : null}
                </>
              ) : (
                <LoadingIcon height="200px" />
              )}
              {!subplotLoading ? (
                subplotData.length ? (
                  <Subplots
                    data={subplotData}
                    handleSubplotPageChange={handleSubplotPageChange}
                    subplotCount={subplotCount}
                    subplotPage={subplotPage}
                    setSubplotPage={setSubplotPage}
                    currentPlot={currentPlot}
                    siteMetadata={siteMetadata}
                  />
                ) : null
              ) : (
                <LoadingIcon height="200px" />
              )}
            </>
          )}
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </ContainerDiv>
  );
};

export default Plots;
